import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Heart,
  ArrowRight,
  Loader2,
  Clock,
  MapPin,
  Briefcase,
  Star,
  Users,
  BookOpen
} from 'lucide-react';
import {
  getSeniorModels,
  registerSeniorProfile,
  getMySeniorProfile,
  getSeniorOpportunities
} from '../services/api';
import { toast } from 'sonner';

const SeniorModels = () => {
  const [models, setModels] = useState([]);
  const [profile, setProfile] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({
    preferred_models: [],
    max_hours_per_week: 20,
    expertise_areas: [],
    availability_start: '',
    mobility: 'lokal',
    notes: ''
  });
  const [newExpertise, setNewExpertise] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modelsRes, profileRes] = await Promise.all([
        getSeniorModels(),
        getMySeniorProfile().catch(() => ({ data: null }))
      ]);
      setModels(modelsRes.data);
      setProfile(profileRes.data);

      if (profileRes.data) {
        const oppRes = await getSeniorOpportunities();
        setOpportunities(oppRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModel = (modelKey) => {
    setFormData(prev => ({
      ...prev,
      preferred_models: prev.preferred_models.includes(modelKey)
        ? prev.preferred_models.filter(m => m !== modelKey)
        : [...prev.preferred_models, modelKey]
    }));
  };

  const addExpertise = () => {
    if (!newExpertise.trim()) return;
    setFormData(prev => ({
      ...prev,
      expertise_areas: [...prev.expertise_areas, newExpertise.trim()]
    }));
    setNewExpertise('');
  };

  const removeExpertise = (idx) => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.filter((_, i) => i !== idx)
    }));
  };

  const handleRegister = async () => {
    if (formData.preferred_models.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Arbeitsmodell');
      return;
    }
    setRegistering(true);
    try {
      const res = await registerSeniorProfile(formData);
      setProfile(res.data);
      setShowRegister(false);
      toast.success('55+ Profil erstellt');
      const oppRes = await getSeniorOpportunities();
      setOpportunities(oppRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setRegistering(false);
    }
  };

  const getMobilityLabel = (m) => {
    const labels = { lokal: 'Lokal', regional: 'Regional', remote: 'Remote' };
    return labels[m] || m;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="senior-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Heart className="w-8 h-8 text-primary" />
            55+ & Übergangsmodelle
          </h1>
          <p className="text-muted-foreground mt-1">
            Flexible Arbeitsmodelle für erfahrene Fachkräfte
          </p>
        </div>

        {profile ? (
          <div className="space-y-6">
            {/* Profile Card */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  Ihr 55+ Profil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <Clock className="w-5 h-5 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Max. Stunden/Woche</p>
                    <p className="font-semibold text-lg">{profile.max_hours_per_week}h</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <MapPin className="w-5 h-5 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Mobilität</p>
                    <p className="font-semibold">{getMobilityLabel(profile.mobility)}</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <Briefcase className="w-5 h-5 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Arbeitsmodelle</p>
                    <p className="font-semibold">{profile.preferred_models?.length || 0} gewählt</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Bevorzugte Arbeitsmodelle</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.preferred_models?.map((m, idx) => {
                      const model = models.find(mod => mod.key === m);
                      return (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {model?.name || m}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                {profile.expertise_areas?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Fachgebiete</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.expertise_areas.map((area, idx) => (
                        <Badge key={idx} className="bg-primary/10 text-primary border-primary/20">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Opportunities */}
            <div>
              <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Passende Angebote ({opportunities?.opportunities?.length || 0})
              </h2>
              {opportunities?.opportunities?.length > 0 ? (
                <div className="grid gap-4">
                  {opportunities.opportunities.map((job, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-all">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-lg">{job.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{job.company_name || 'Unternehmen'}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" />
                          {job.location || job.city || 'Standort nicht angegeben'}
                        </p>
                        {job.required_competencies?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {job.required_competencies.slice(0, 5).map((comp, cidx) => (
                              <Badge key={cidx} variant="outline" className="text-xs">{comp}</Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Noch keine passenden Angebote. Sobald Arbeitgeber entsprechende Stellen veröffentlichen, finden Sie diese hier.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <div>
            {!showRegister ? (
              <div className="space-y-6">
                {/* Models Overview */}
                <div className="grid md:grid-cols-3 gap-4">
                  {models.map((model) => (
                    <Card key={model.key} className="hover:shadow-md hover:border-primary/20 transition-all">
                      <CardContent className="pt-6 text-center">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                          {model.key === 'mentoring' ? <Users className="w-6 h-6 text-primary" /> :
                           model.key === 'beratung' ? <BookOpen className="w-6 h-6 text-primary" /> :
                           <Briefcase className="w-6 h-6 text-primary" />}
                        </div>
                        <h3 className="font-semibold mb-1">{model.name}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{model.desc}</p>
                        <Badge variant="outline">{model.hours}</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <h3 className="text-lg font-semibold mb-2">Profil erstellen</h3>
                    <p className="text-muted-foreground max-w-md mx-auto mb-4">
                      Erstellen Sie Ihr 55+ Profil und finden Sie passende Arbeitsmodelle.
                    </p>
                    <Button onClick={() => setShowRegister(true)} className="rounded-full" data-testid="start-senior-btn">
                      55+ Profil erstellen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>55+ Profil erstellen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Model Selection */}
                  <div className="space-y-3">
                    <Label>Bevorzugte Arbeitsmodelle *</Label>
                    <div className="grid md:grid-cols-3 gap-3">
                      {models.map(model => (
                        <div
                          key={model.key}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            formData.preferred_models.includes(model.key)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => toggleModel(model.key)}
                          data-testid={`model-${model.key}`}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.preferred_models.includes(model.key)}
                              className="pointer-events-none"
                            />
                            <div>
                              <p className="font-medium text-sm">{model.name}</p>
                              <p className="text-xs text-muted-foreground">{model.hours}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="space-y-2">
                    <Label>Max. Stunden pro Woche: {formData.max_hours_per_week}h</Label>
                    <Slider
                      value={[formData.max_hours_per_week]}
                      onValueChange={(v) => setFormData({ ...formData, max_hours_per_week: v[0] })}
                      min={5} max={40} step={5}
                    />
                  </div>

                  {/* Mobility */}
                  <div className="space-y-2">
                    <Label>Mobilität</Label>
                    <Select
                      value={formData.mobility}
                      onValueChange={(v) => setFormData({ ...formData, mobility: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lokal">Lokal (Wohnort)</SelectItem>
                        <SelectItem value="regional">Regional (bis 50km)</SelectItem>
                        <SelectItem value="remote">Remote / Home Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Expertise */}
                  <div className="space-y-2">
                    <Label>Fachgebiete</Label>
                    {formData.expertise_areas.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.expertise_areas.map((area, idx) => (
                          <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => removeExpertise(idx)}>
                            {area} x
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        value={newExpertise}
                        onChange={(e) => setNewExpertise(e.target.value)}
                        placeholder="z.B. Projektmanagement, Maschinenbau..."
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                      />
                      <Button variant="outline" onClick={addExpertise}>Hinzufügen</Button>
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="space-y-2">
                    <Label>Verfügbar ab</Label>
                    <Input
                      type="date"
                      value={formData.availability_start}
                      onChange={(e) => setFormData({ ...formData, availability_start: e.target.value })}
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Anmerkungen</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Weitere Wünsche oder Informationen..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setShowRegister(false)} className="rounded-full">
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleRegister}
                      disabled={registering || formData.preferred_models.length === 0}
                      className="rounded-full flex-1"
                      data-testid="submit-senior-btn"
                    >
                      {registering && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Profil erstellen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SeniorModels;
